import React, { useEffect, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Breadcrumb, BreadcrumbLinkMouseEvent } from "@progress/kendo-react-layout";
import { SvgIcon } from "@progress/kendo-react-common";
import { folderIcon } from "@progress/kendo-svg-icons";
import { getUploadErrorMessage } from "@presentation/common/ErrorHelpers";
import type { PathTypeSchema } from "@domain/schemas/BI";

export interface UploadDestination {
  rootPath: string;
  folder: string;
}

interface UploadDestinationPickerDialogProperties {
  fileName: string;
  pathType: PathTypeSchema;
  locationName: string;
  /** Every resolved root configured for this project/facet - empty means no override, just the
  deployment-wide default. */
  roots: string[];
  initialDestination?: Partial<UploadDestination>;
  listFoldersAsync: (pathType: PathTypeSchema, folder: string) => Promise<string[]>;
  onContinue: (destination: UploadDestination) => void;
  onCancel: () => void;
}

// Kendo's BreadcrumbLink only dispatches `onItemSelect` when the clicked item's `id` is truthy, so
// neither the location crumb nor an at-the-top-of-its-root segment can use an empty-string id
// (never fires) - sentinels that can't collide with a real relative-folder path.
const LOCATION_BREADCRUMB_ID = "<location>";
const ROOT_SEGMENT_ID = "<root>";

const joinPath = (root: string, relativeFolder: string): string => [root, relativeFolder].filter(Boolean).join("/");

/**
Lets the user choose both which configured root (when a project has more than one for this facet)
and which subfolder within that root to upload to - without this, uploads could only ever land at a
root's top level, with no way to target an existing subfolder structure. Mirrors the main file
explorer's root-choice-at-the-location pattern: with 2+ roots, the location root shows each root as
a selectable entry (no ambiguous merged listing); with 0/1 roots, the single root is pre-selected.
The breadcrumb keeps the location label and configured root visible in the same order as the data
explorer.
*/
export const UploadDestinationPickerDialog: React.FC<UploadDestinationPickerDialogProperties> = ({
  fileName,
  pathType,
  locationName,
  roots,
  initialDestination,
  listFoldersAsync,
  onContinue,
  onCancel,
}) => {
  const initialRootIsConfigured = initialDestination?.rootPath !== undefined && roots.includes(initialDestination.rootPath);
  const defaultRoot = roots.length > 1 ? null : (roots[0] ?? "");
  const selectedInitialRoot = initialRootIsConfigured ? (initialDestination?.rootPath ?? "") : defaultRoot;
  const [selectedRoot, setSelectedRoot] = useState<string | null>(selectedInitialRoot);
  const [relativeFolder, setRelativeFolder] = useState(initialDestination?.folder && (initialRootIsConfigured || roots.length <= 1) ? initialDestination.folder : "");
  const [subfolders, setSubfolders] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (selectedRoot === null) {
      // At the root-choice screen - nothing to list yet, the roots themselves are the rows.
      setSubfolders([]);
      setLoading(false);
      return;
    }

    let isCancelled = false;

    const loadFolders = async () => {
      setLoading(true);
      setError("");
      setSubfolders([]);
      try {
        const folders = await listFoldersAsync(pathType, joinPath(selectedRoot, relativeFolder));
        if (!isCancelled) {
          const rootPrefix = selectedRoot ? `${selectedRoot}/` : "";
          setSubfolders(folders.map((folder) => (folder.startsWith(rootPrefix) ? folder.slice(rootPrefix.length) : folder)));
        }
      } catch (error) {
        if (!isCancelled) {
          setError(getUploadErrorMessage(error));
          setSubfolders([]);
        }
      }
      if (!isCancelled) setLoading(false);
    };

    void loadFolders();
    return () => {
      isCancelled = true;
    };
  }, [pathType, selectedRoot, relativeFolder, listFoldersAsync, retry]);

  const handleRootChosen = (root: string) => {
    setSelectedRoot(root);
    setRelativeFolder("");
  };

  const breadcrumbItems = React.useMemo(() => {
    const items = [{ id: LOCATION_BREADCRUMB_ID, text: `Location: ${locationName}` }];
    if (selectedRoot === null) return items;

    if (selectedRoot) items.push({ id: ROOT_SEGMENT_ID, text: selectedRoot });
    if (!relativeFolder) return items;

    let accumulated = "";
    for (const segment of relativeFolder.split("/")) {
      accumulated = accumulated ? `${accumulated}/${segment}` : segment;
      items.push({ id: accumulated, text: segment });
    }
    return items;
  }, [locationName, relativeFolder, selectedRoot]);

  const handleBreadcrumbClick = (event: BreadcrumbLinkMouseEvent) => {
    const id = String(event.id ?? "");
    if (id === LOCATION_BREADCRUMB_ID) {
      // Back to the location root: with 2+ roots that means the root-choice screen again, otherwise there's only
      // ever one root to be at, so just reset the relative folder.
      if (roots.length > 1) setSelectedRoot(null);
      setRelativeFolder("");
      return;
    }
    if (id === ROOT_SEGMENT_ID) {
      setRelativeFolder("");
      return;
    }
    setRelativeFolder(id);
  };

  return (
    <Dialog title="Choose upload destination" onClose={onCancel} width={520} height={560}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "100%" }}>
        <p style={{ margin: 0 }}>
          Choose where <strong>{fileName}</strong> should be uploaded:
        </p>
        <Breadcrumb data={breadcrumbItems} onItemSelect={handleBreadcrumbClick} size="medium" />
        {error && (
          <div role="alert" style={{ color: "#b91c1c", whiteSpace: "pre-wrap" }}>
            {error}
            <br />
            <Button type="button" onClick={() => setRetry((value) => value + 1)}>
              Retry
            </Button>
          </div>
        )}
        <div style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 4 }}>
          {selectedRoot === null ? (
            roots.map((root) => (
              <div
                key={root}
                role="button"
                tabIndex={0}
                onClick={() => handleRootChosen(root)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleRootChosen(root);
                }}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
              >
                <SvgIcon icon={folderIcon} />
                {root || "Default directory"}
              </div>
            ))
          ) : loading ? (
            <p style={{ padding: 12, color: "#64748b" }}>Loading…</p>
          ) : subfolders.length === 0 ? ( // eslint-disable-line unicorn/no-nested-ternary -- four mutually exclusive render states (root-choice/loading/empty/list)
            <p style={{ padding: 12, color: "#64748b" }}>{error ? "Directories could not be loaded." : "No subfolders here."}</p>
          ) : (
            subfolders.map((folder) => (
              <div
                key={folder}
                role="button"
                tabIndex={0}
                onClick={() => setRelativeFolder(folder)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") setRelativeFolder(folder);
                }}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
              >
                <SvgIcon icon={folderIcon} />
                {folder.split("/").at(-1)}
              </div>
            ))
          )}
        </div>
      </div>
      <DialogActionsBar>
        <Button onClick={onCancel}>Cancel</Button>
        <Button themeColor="primary" disabled={selectedRoot === null || loading || Boolean(error)} onClick={() => onContinue({ rootPath: selectedRoot ?? "", folder: relativeFolder })}>
          Select directory
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};
